/**
 * Hook for playing notification sounds using Web Audio API
 * No external audio files needed - generates sounds programmatically
 */

import { useCallback, useRef } from "react";

type SoundType = "ding" | "chime" | "bell";

interface UseNotificationSoundOptions {
  volume?: number;
  type?: SoundType;
}

export function useNotificationSound(options: UseNotificationSoundOptions = {}) {
  const { volume = 0.5, type = "chime" } = options;
  const audioContextRef = useRef<AudioContext | null>(null);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      )();
    }
    return audioContextRef.current;
  }, []);

  const playDing = useCallback(
    (ctx: AudioContext) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.setValueAtTime(880, ctx.currentTime); // A5
      oscillator.type = "sine";

      gainNode.gain.setValueAtTime(volume, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.5);
    },
    [volume]
  );

  const playChime = useCallback(
    (ctx: AudioContext) => {
      // Two-tone chime (like a doorbell)
      const frequencies = [659.25, 523.25]; // E5, C5
      const durations = [0.15, 0.3];

      frequencies.forEach((freq, index) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.frequency.setValueAtTime(freq, ctx.currentTime);
        oscillator.type = "sine";

        const startTime = ctx.currentTime + index * 0.2;
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + durations[index]);

        oscillator.start(startTime);
        oscillator.stop(startTime + durations[index]);
      });
    },
    [volume]
  );

  const playBell = useCallback(
    (ctx: AudioContext) => {
      // Bell-like sound with harmonics
      const fundamental = 440; // A4
      const harmonics = [1, 2, 3, 4.2, 5.4];
      const amplitudes = [1, 0.6, 0.4, 0.25, 0.2];

      harmonics.forEach((harmonic, index) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.frequency.setValueAtTime(fundamental * harmonic, ctx.currentTime);
        oscillator.type = "sine";

        const amp = volume * amplitudes[index];
        gainNode.gain.setValueAtTime(amp, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1);

        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 1);
      });
    },
    [volume]
  );

  const play = useCallback(
    (soundType?: SoundType) => {
      try {
        const ctx = getAudioContext();

        // Resume context if suspended (required for autoplay policy)
        if (ctx.state === "suspended") {
          ctx.resume();
        }

        const selectedType = soundType || type;

        switch (selectedType) {
          case "ding":
            playDing(ctx);
            break;
          case "bell":
            playBell(ctx);
            break;
          case "chime":
          default:
            playChime(ctx);
            break;
        }
      } catch {
        // Silently fail if audio is not supported
      }
    },
    [getAudioContext, type, playDing, playChime, playBell]
  );

  return { play };
}
